import React, { useMemo, useState, useEffect } from 'react';
import { DEFAULT_TREND_YEARS } from './areaPriceTrend.js';
import SaleDot from './SaleDot.js';

// The listing popup's area price-development chart (design C): a small line chart with a segmented
// 1 / 2 / 3 / Max year picker (default 3 years). An "Området / Denna bostad" toggle (when the home has an
// estimate) overlays this address's own modelled value curve with an uncertainty band (estimate lo/hi) on
// top of the area average; the home's own kr/m² shows as a ring and its earlier sales (ownSales) as dots in
// both views (SaleDot: hover or tap one for its date, price, kr/m², rooms and the change since). Picking a window rescales the curve and recomputes the headline change. Styled by .lfv-pc* in
// priceHistory.css. Renders nothing without at least two points.
//
// series:   [{ period:'YYYYQn', year, sqmPrice }] oldest first (listing.priceHistory.area.series)
// estimate: [{ period, sqmPrice, lo, hi }] oldest first (listing.priceHistory.estimate) | null
// ownSales: [{ period, sqmPrice, date?, price?, rooms? }] this home's (or a shared address's) sales | null

const RANGES = [
    { key: '1', years: 1 },
    { key: '2', years: 2 },
    { key: '3', years: 3 },
    { key: 'max', years: null },
];
const DEFAULT_KEY = String(DEFAULT_TREND_YEARS); // '3'

const W = 380;
const H = 76;
const PAD = { l: 5, r: 16, t: 16, b: 8 };

export default function AreaPriceChart({ series, estimate, homeSqm, ownSales, swedish, formatInt }) {
    const fmt = formatInt || ((n) => String(n));
    const hasHome = Array.isArray(estimate) && estimate.length >= 2 && Number.isFinite(homeSqm) && homeSqm > 0;

    const [rangeKey, setRangeKey] = useState(DEFAULT_KEY);
    const [view, setView] = useState(hasHome ? 'home' : 'area');
    // A different listing (new series/estimate) resets the controls to their defaults.
    useEffect(() => {
        setRangeKey(DEFAULT_KEY);
        setView(hasHome ? 'home' : 'area');
    }, [series, estimate]); // eslint-disable-line react-hooks/exhaustive-deps

    const model = useMemo(() => {
        if (!Array.isArray(series) || series.length < 2) return null;
        const range = RANGES.find((r) => r.key === rangeKey) || RANGES[2];
        const back = range.years == null ? series.length : range.years * 4;
        const startIdx = Math.max(0, (series.length - 1) - back);
        const areaWin = series.slice(startIdx);
        const n = areaWin.length;

        // The home's modelled value per window quarter, aligned to the area window by period.
        const estByPeriod = hasHome ? new Map(estimate.map((e) => [e.period, e])) : null;
        const homePts = hasHome
            ? areaWin.map((p, i) => { const e = estByPeriod.get(p.period); return e ? { i, v: e.sqmPrice, lo: e.lo, hi: e.hi } : null; }).filter(Boolean)
            : [];
        // This exact home's earlier sales that fall inside the window.
        const periodIndex = new Map(areaWin.map((p, i) => [p.period, i]));
        const sales = Array.isArray(ownSales)
            ? ownSales.filter((s) => periodIndex.has(s.period)).map((s) => { const i = periodIndex.get(s.period); return { i, v: s.sqmPrice, year: areaWin[i].year, sale: s }; })
            : [];

        // Scale from the union of everything drawable, so toggling the view never rescales the curve.
        const scaleVals = areaWin.map((p) => p.sqmPrice);
        if (hasHome) { homePts.forEach((h) => scaleVals.push(h.lo, h.hi)); scaleVals.push(homeSqm); }
        sales.forEach((s) => scaleVals.push(s.v));
        const lo = Math.min(...scaleVals);
        const hi = Math.max(...scaleVals);
        const span = (hi - lo) || 1;
        const min = lo - span * 0.08;
        const max = hi + span * 0.08;
        const x = (i) => PAD.l + (i / (n - 1 || 1)) * (W - PAD.l - PAD.r);
        const y = (v) => PAD.t + (1 - (v - min) / (max - min)) * (H - PAD.t - PAD.b);

        const areaLine = areaWin.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.sqmPrice).toFixed(1)}`).join('');
        const areaFill = `${areaLine}L${x(n - 1).toFixed(1)},${H - PAD.b}L${x(0).toFixed(1)},${H - PAD.b}Z`;
        const grid = areaWin
            .map((p, i) => (p.period.endsWith('Q1') ? x(i) : null))
            .filter((gx) => gx != null)
            .map((gx) => <line key={gx} x1={gx.toFixed(1)} y1={PAD.t} x2={gx.toFixed(1)} y2={H - PAD.b} />);

        let homeLine = null;
        let bandPath = null;
        if (hasHome && homePts.length >= 2) {
            homeLine = homePts.map((h, k) => `${k ? 'L' : 'M'}${x(h.i).toFixed(1)},${y(h.v).toFixed(1)}`).join('');
            const top = homePts.map((h, k) => `${k ? 'L' : 'M'}${x(h.i).toFixed(1)},${y(h.hi).toFixed(1)}`).join('');
            const bot = homePts.slice().reverse().map((h) => `${x(h.i).toFixed(1)},${y(h.lo).toFixed(1)}`).join('L');
            bandPath = `${top}L${bot}Z`;
        }

        const pos = (xx, yy) => ({ left: `${(xx / W * 100).toFixed(2)}%`, top: `${(yy / H * 100).toFixed(2)}%` });
        const homeMarker = hasHome ? pos(x(n - 1), y(homeSqm)) : null;
        // Each dot's card says how prices moved since: this home's asking kr/m² today against the sale, or with
        // no home (an address's sales on the area page) the area's curve since that quarter.
        const saleMarkers = sales.map((s, k) => ({
            key: `${s.i}-${s.v}-${k}`,
            style: pos(x(s.i), y(s.v)),
            sale: s.sale,
            since: hasHome
                ? { label: swedish ? 'Idag' : 'Today', pct: Math.round((homeSqm / s.v - 1) * 100), suffix: swedish ? 'per m²' : 'per m²' }
                : { label: swedish ? 'Området' : 'The area', pct: Math.round((areaWin[n - 1].sqmPrice / areaWin[s.i].sqmPrice - 1) * 100), suffix: swedish ? 'sedan dess' : 'since then' },
        }));

        // Headline value and change for the active view. The area uses the window's change; "Denna bostad"
        // uses THIS home's own price gain since its most recent prior sale in the window (ownSales) — not the
        // area's trend. With no prior sale in the window there's no real home gain, so no change is shown.
        const homeView = view === 'home' && hasHome && homePts.length >= 2;
        let change;
        let sinceYear = null;
        if (homeView) {
            const lastSale = sales.reduce((best, s) => (!best || s.i > best.i ? s : best), null);
            change = lastSale ? Math.round((homeSqm / lastSale.v - 1) * 100) : null;
            sinceYear = lastSale ? lastSale.year : null;
        } else {
            change = Math.round((areaWin[n - 1].sqmPrice / areaWin[0].sqmPrice - 1) * 100);
        }
        const latest = homeView ? homeSqm : areaWin[n - 1].sqmPrice;

        return { startYear: areaWin[0].year, change, sinceYear, latest, homeView, areaLine, areaFill, grid, homeLine, bandPath, homeMarker, saleMarkers, hasSales: sales.length > 0 };
    }, [series, estimate, homeSqm, ownSales, rangeKey, view, hasHome, swedish]);

    if (!model) return null;
    const showHome = model.homeView;
    const up = (model.change ?? 0) >= 0;
    const arrow = up ? '↗' : '↘';
    const sign = up ? '+' : '−';

    return (
        <div className="lfv-pricechart">
            <div className="lfv-pc-head">
                {hasHome && (
                    <div className="lfv-pc-seg lfv-pc-view" role="tablist" aria-label={swedish ? 'Vad som visas' : 'What to show'}>
                        <button type="button" role="tab" aria-selected={!showHome} className={showHome ? '' : 'is-on'} onClick={() => setView('area')}>
                            {swedish ? 'Området' : 'Area'}
                        </button>
                        <button type="button" role="tab" aria-selected={showHome} className={showHome ? 'is-on' : ''} onClick={() => setView('home')}>
                            {swedish ? 'Denna bostad' : 'This home'}
                        </button>
                    </div>
                )}
                <div className="lfv-pc-headline">
                    <b className="lfv-pc-latest">{fmt(model.latest)} kr/m²</b>
                    {model.change != null && (
                        <span className={`lfv-pc-delta${up ? '' : ' is-down'}`}>
                            {arrow} {sign}{Math.abs(model.change)} %
                            {model.sinceYear ? <span className="lfv-pc-since">{swedish ? ` sedan ${model.sinceYear}` : ` since ${model.sinceYear}`}</span> : null}
                        </span>
                    )}
                </div>
                <div className="lfv-pc-seg lfv-pc-years" role="tablist" aria-label={swedish ? 'Tidsspann' : 'Time range'}>
                    {RANGES.map((r) => (
                        <button
                            key={r.key}
                            type="button"
                            role="tab"
                            aria-selected={r.key === rangeKey}
                            className={r.key === rangeKey ? 'is-on' : ''}
                            onClick={() => setRangeKey(r.key)}
                        >
                            {r.key === 'max' ? 'Max' : `${r.years} ${swedish ? 'år' : 'yr'}`}
                        </button>
                    ))}
                </div>
            </div>
            <div className="lfv-pc-plot">
                <svg className="lfv-pc-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
                    <g className="lfv-pc-grid">{model.grid}</g>
                    {showHome ? (
                        <>
                            {model.bandPath && <path className="lfv-pc-band" d={model.bandPath} />}
                            <path className="lfv-pc-ref" d={model.areaLine} />
                            {model.homeLine && <path className="lfv-pc-line" d={model.homeLine} />}
                        </>
                    ) : (
                        <>
                            <path className="lfv-pc-fill" d={model.areaFill} />
                            <path className="lfv-pc-line" d={model.areaLine} />
                        </>
                    )}
                </svg>
                {model.saleMarkers.map((s) => (
                    <SaleDot key={`s${s.key}`} className="lfv-pc-mk lfv-pc-mk-sold" style={s.style} sale={s.sale} since={s.since} swedish={swedish} />
                ))}
                {model.homeMarker && <span className="lfv-pc-mk lfv-pc-mk-home" style={model.homeMarker} />}
            </div>
            {/* The axis's two ends with the legend between them, on one row. */}
            <div className="lfv-pc-axis">
                <span aria-hidden="true">{model.startYear}</span>
                <div className="lfv-pc-foot">
                    {showHome ? (
                        <>
                            {model.bandPath ? <span><i className="lfv-pc-sw lfv-pc-sw-band" />{swedish ? 'Bostadens värde' : 'Home value'}</span> : null}
                            <span><i className="lfv-pc-sw lfv-pc-sw-ref" />{swedish ? 'Områdets snitt' : 'Area average'}</span>
                        </>
                    ) : (
                        <span><i className="lfv-pc-sw lfv-pc-sw-line" />{swedish ? 'Områdets snitt' : 'Area average'}</span>
                    )}
                    {hasHome && !showHome ? <span><i className="lfv-pc-sw lfv-pc-sw-home" />{swedish ? 'Denna bostad' : 'This home'} {fmt(homeSqm)} kr</span> : null}
                    {model.hasSales ? <span><i className="lfv-pc-sw lfv-pc-sw-sold" />{swedish ? 'Tidigare sålt' : 'Sold before'}</span> : null}
                </div>
                <span aria-hidden="true">{swedish ? 'Idag' : 'Now'}</span>
            </div>
        </div>
    );
}
